import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  api, ApiError, errMsg, makeIdem,
  type Account, type BankLine, type BankStatementDetail, type Category, type CounterpartyPage, type FromLineTransaction, type FromLineTransfer, type TransferQuote,
} from "../../api";
import { Modal } from "../../components/Modal";
import { RateInfo } from "../../components/TransferForm";
import { Btn, Field, Input, Notice, Select } from "../../components/ui";
import { fmtDay, formatMoney, normalizeMoneyInput } from "../../lib/money";

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const idemHdr = (i: ReturnType<typeof makeIdem>) => ({ "idempotency-key": i.get()["idempotency-key"] });
type Confirm = null | "duplicate" | "outside" | "atypical";

/**
 * DEC-075 / DEC-113 — criar uma transação ou transferência A PARTIR de uma linha pendente do extrato. O formulário vem pré-preenchido com os dados da
 * linha (conta, valor e sinal não se alteram); a pessoa revisa, completa o que é obrigatório e confirma. O servidor cria a movimentação já confirmada e
 * a concilia com a linha numa única operação: a tela só mostra o sucesso depois da resposta do servidor e, em qualquer recusa, nada foi criado.
 */
export function CreateFromLineDialog({ realm, d, line, onClose, onDone, onConflict }: {
  realm: string; d: BankStatementDetail; line: BankLine; onClose: () => void; onDone: (text: string) => void; onConflict: (e: unknown) => void;
}) {
  const out = line.amount.cents < 0;
  const abs = line.amount.decimal.replace("-", "");
  const [mode, setMode] = useState<"tx" | "tr">("tx");
  const idem = useRef(makeIdem());
  const [description, setDescription] = useState(line.description);
  const [date, setDate] = useState(line.date);
  const [notes, setNotes] = useState("");
  const [cats, setCats] = useState<Category[]>([]);
  const [cps, setCps] = useState<CounterpartyPage["items"]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categoryId, setCategoryId] = useState("");
  const [counterpartyId, setCounterpartyId] = useState("");
  const [otherId, setOtherId] = useState("");
  const [otherAmount, setOtherAmount] = useState("");
  const [quote, setQuote] = useState<TransferQuote | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [days, setDays] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    void Promise.all([
      api<Category[]>("GET", `/api/${realm}/categories?status=ativa`),
      api<CounterpartyPage>("GET", `/api/${realm}/counterparties?status=ativa&limit=200`),
      api<Account[]>("GET", `/api/${realm}/accounts?entityId=${d.entityId}&status=ativa`),
    ]).then(([c, p, a]) => { if (!alive) return; setCats(c); setCps(p.items); setAccounts(a.filter((x) => x.id !== d.accountId)); })
      .catch((e) => { if (alive) setErr(errMsg(e)); });
    return () => { alive = false; };
  }, [realm, d.entityId, d.accountId]);

  const options = useMemo(() => {
    const byId = new Map(cats.map((c) => [c.id, c]));
    const path = (c: Category): string => (c.parentId && byId.get(c.parentId) ? `${path(byId.get(c.parentId)!)} › ${c.name}` : c.name);
    return cats.filter((c) => c.level >= 2 && c.nature === (out ? "despesa" : "receita") && !c.systemKey).map((c) => ({ id: c.id, label: path(c) })).sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [cats, out]);
  const other = accounts.find((a) => a.id === otherId);
  const cross = !!other && other.currency !== d.currency;
  const otherNorm = normalizeMoneyInput(otherAmount);
  const positive = (n: string | null) => n !== null && !n.startsWith("-") && !/^0+(\.0+)?$/.test(n);

  // taxa efetiva: calculada pelo servidor (a tela só exibe)
  useEffect(() => {
    setQuote(null);
    if (mode !== "tr" || !cross || !other || !positive(otherNorm)) return;
    const qs = new URLSearchParams(out
      ? { fromAccountId: d.accountId, toAccountId: other.id, fromAmount: abs, toAmount: otherNorm! }
      : { fromAccountId: other.id, toAccountId: d.accountId, fromAmount: otherNorm!, toAmount: abs });
    const h = setTimeout(() => { void api<TransferQuote>("GET", `/api/${realm}/transfers/quote?${qs}`).then(setQuote).catch(() => setQuote(null)); }, 300);
    return () => clearTimeout(h);
  }, [realm, mode, cross, other, otherNorm, out, abs, d.accountId]);   // eslint-disable-line react-hooks/exhaustive-deps

  function reset(next: "tx" | "tr") { setMode(next); setErr(null); setConfirm(null); }

  async function submit(e: FormEvent | null, flag: Confirm = null) {
    e?.preventDefault();
    setErr(null);
    if (!description.trim() && mode === "tx") return setErr("Informe a descrição.");
    if (mode === "tx" && !categoryId) return setErr("Escolha a categoria: ela é obrigatória em todas as transações.");
    if (mode === "tr") {
      if (!otherId) return setErr("Escolha a outra conta da transferência.");
      if (cross && !positive(otherNorm)) return setErr(`Informe o valor na outra conta (${other?.currency}), como consta no extrato dela.`);
    }
    const confirmFlags = { ...(flag === "duplicate" ? { confirmDuplicate: true } : {}), ...(flag === "outside" ? { confirmOutsideWindow: true } : {}), ...(flag === "atypical" ? { confirmAtypicalRate: true } : {}) };
    setBusy(true);
    try {
      if (mode === "tx") {
        const r = await api<FromLineTransaction>("POST", `/api/${realm}/bank-statement-lines/${line.id}/create-transaction`, {
          version: line.version, categoryId, description: description.trim(), settledDate: date, counterpartyId: counterpartyId || null, notes: notes.trim() || null,
          ...(flag === "duplicate" ? { confirmDuplicate: true } : {}), ...(flag === "outside" ? { confirmOutsideWindow: true } : {}),
        }, idemHdr(idem.current));
        onDone(`${r.transaction.kind === "despesa" ? "Despesa" : "Receita"} “${r.transaction.description}” criada e conciliada com a linha do extrato.`);
      } else {
        const r = await api<FromLineTransfer>("POST", `/api/${realm}/bank-statement-lines/${line.id}/create-transfer`, {
          version: line.version, otherAccountId: otherId, ...(cross ? { otherAmount: otherNorm } : {}), description: description.trim() || null, settledDate: date, notes: notes.trim() || null, ...confirmFlags,
        }, idemHdr(idem.current));
        onDone(`Transferência criada e conciliada com a linha (ponta de ${r.leg === "saida" ? "saída" : "entrada"}). A outra ponta continua pendente de conciliação na conta ${r.leg === "saida" ? r.transfer.toAccountName : r.transfer.fromAccountName}.`);
      }
    } catch (x) {
      if (x instanceof ApiError) idem.current.renew();
      if (x instanceof ApiError && x.code === "possible_duplicate") { setConfirm("duplicate"); setErr(null); }
      else if (x instanceof ApiError && x.code === "reconciliation_outside_window_confirmation_required") { setDays(Number(x.extra.days)); setConfirm("outside"); setErr(null); }
      else if (x instanceof ApiError && x.code === "atypical_rate") { setConfirm("atypical"); setErr(null); }
      else if (x instanceof ApiError && (x.code === "version_conflict" || x.code === "reconciliation_line_taken" || x.code === "statement_not_open")) onConflict(x);
      else setErr(errMsg(x));
    } finally { setBusy(false); }
  }

  const confirmText: Record<Exclude<Confirm, null>, string> = {
    duplicate: mode === "tx" ? "Já existe um lançamento igual (mesma conta, tipo, valor, data e descrição). Se é uma ocorrência diferente, confirme para criar mesmo assim." : "Já existe uma transferência igual (mesmas contas, valores e data). Se é uma ocorrência diferente, confirme para criar mesmo assim.",
    outside: `A data informada difere ${days ?? "mais de 3"} dias da data da linha do extrato, acima da janela de 3 dias. Confirme apenas se é a mesma movimentação.`,
    atypical: "A taxa efetiva calculada está fora da faixa de referência (0,01 a 1.000 BRL por 1 USD). Os valores serão gravados como estão e a confirmação ficará registrada na auditoria.",
  };

  return (
    <Modal title="Criar a partir da linha do extrato" onClose={onClose} wide>
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="muted small">Linha do banco · {d.accountName}</div>
        <strong>{line.description}</strong>
        <div className="muted small">{fmtDay(line.date)}</div>
        <div className={`tabular ${out ? "neg" : "pos"}`}>{out ? "−" : "+"} {formatMoney(abs, d.currency)}</div>
      </div>
      <div className="seg" role="group" aria-label="O que criar" style={{ marginBottom: 12 }}>
        <button type="button" aria-pressed={mode === "tx"} onClick={() => reset("tx")} data-create-kind="tx">{out ? "Despesa" : "Receita"}</button>
        <button type="button" aria-pressed={mode === "tr"} onClick={() => reset("tr")} data-create-kind="tr">Transferência</button>
      </div>
      <form onSubmit={(e) => void submit(e)} noValidate data-create-from-line>
        {err && <Notice kind="error">{err}</Notice>}
        {confirm && (
          <Notice kind="error">
            <span data-create-confirm-text>{confirmText[confirm]}</span>
            <div className="row" style={{ marginTop: 8 }}>
              <Btn variant="danger" disabled={busy} onClick={() => void submit(null, confirm)} data-create-confirm={confirm}>Confirmar e criar</Btn>
              <Btn variant="ghost" onClick={() => setConfirm(null)}>Revisar</Btn>
            </div>
          </Notice>
        )}
        {mode === "tx" ? (
          <p className="small muted" style={{ marginTop: 0 }}>Cria {out ? "uma despesa" : "uma receita"} de {formatMoney(abs, d.currency)} na conta {d.accountName}, já efetivada, e a concilia com esta linha. O valor, o sinal e a conta vêm da linha e não mudam.</p>
        ) : (
          <p className="small muted" style={{ marginTop: 0 }}>
            Esta linha corresponde à <strong>ponta de {out ? "saída" : "entrada"}</strong> ({out ? `sai de ${d.accountName}` : `entra em ${d.accountName}`}). Só essa ponta será conciliada; a outra ponta continuará pendente na outra conta.
          </p>
        )}
        <Field label="Descrição" required><Input value={description} onChange={(e) => { setDescription(e.target.value); setConfirm(null); }} maxLength={200} name="createDescription" autoComplete="off" /></Field>
        {mode === "tx" ? (
          <div className="grid2">
            <Field label="Categoria" required>
              <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} name="createCategory" required>
                <option value="">Selecione…</option>
                {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </Select>
            </Field>
            <Field label={out ? "Beneficiário" : "Pagador"}>
              <Select value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)} name="createCounterparty">
                <option value="">—</option>
                {cps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
          </div>
        ) : (
          <>
            <div className="grid2">
              <Field label={out ? "Conta de destino" : "Conta de origem"}>
                <Select value={otherId} onChange={(e) => { setOtherId(e.target.value); setConfirm(null); }} name="createOtherAccount">
                  <option value="">Selecione…</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
                </Select>
              </Field>
              {cross
                ? <Field label={`Valor na outra conta (${other!.currency})`}><Input value={otherAmount} onChange={(e) => { setOtherAmount(e.target.value); setConfirm(null); }} inputMode="decimal" decimals={2} placeholder="0,00" name="createOtherAmount" /></Field>
                : <Field label={`Valor na outra conta (${other?.currency ?? d.currency})`}><Input value={abs.replace(".", ",")} readOnly aria-readonly name="createOtherSame" /></Field>}
            </div>
            {cross && <p className="small muted" style={{ margin: "-4px 0 8px" }}>Informe o valor como consta no extrato da outra conta. A taxa efetiva é calculada automaticamente.</p>}
            {quote?.crossCurrency && <RateInfo rateDisplay={quote.rateDisplay} unit={quote.rateUnit} />}
          </>
        )}
        <div className="grid2">
          <Field label="Data efetiva"><Input type="date" value={date} max={today()} onChange={(e) => { setDate(e.target.value); setConfirm(null); }} name="createDate" required /></Field>
          <Field label="Observações"><Input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} name="createNotes" /></Field>
        </div>
        <div className="modal-actions">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <button type="submit" className="btn primary" disabled={busy} data-create-submit>{busy ? "Criando…" : mode === "tx" ? "Criar e conciliar" : "Criar transferência e conciliar"}</button>
        </div>
      </form>
    </Modal>
  );
}
